import { el, clear } from '../../core/dom.js';
import { navigate } from '../../core/router.js';
import { skeleton } from '../../components/skeleton.js';
import { toast } from '../../components/toast.js';
import {
  acceptSaved, loadProduct, refreshShellProducts, saveProduct, syncWizardUrl,
} from './actions.js';
import { renderConnectorsStep } from './connectors.js';
import { renderConnectStep } from './connect.js';
import { renderDetails } from './details.js';
import { renderFiles } from './files.js';
import {
  applyDraft, freshState, IN_FLOW_SOURCES, itemForSource, progressPct,
  readUrlState, showWelcomeBadge, stepItems, wizardCrumb, writeDraft,
} from './model.js';
import { loadConnectorBundle } from './actions.js';
import { assembleCatalogue } from '../connectors/catalogue.js';

/**
 * @param {{ me: object, productId: string, productName: string, products?: object[] }} opts
 */
export function renderNewProduct(opts) {
  const root = el('div', { class: 'np' });
  const state = freshState();
  const url = readUrlState(window.location.search);
  if (url.productId) {
    state.productId = url.productId;
    state.step = url.step > 1 ? url.step : 1;
    state.connectSource = url.connectSource || '';
  } else {
    applyDraft(state);
    state.step = 1;
    state.connectSource = '';
  }

  const ui = {
    root,
    state,
    opts,
    dead: false,
    saving: false,
    loading: !!url.productId,
    catalogue: [],
    head: el('div'),
    rail: el('div'),
    body: el('div', { class: 'np-body' }),
  };

  root.append(
    ui.head,
    el('div', { class: 'np-layout' }, ui.rail, ui.body),
  );
  root.addEventListener('bonnie:unmount', () => { ui.dead = true; });

  // Tell the shell when crumb / welcome / sidebar mode changes.
  emitShellMeta(ui);

  if (ui.loading) {
    paint(ui);
    boot(ui);
  } else {
    paint(ui);
  }
  return root;
}

async function boot(ui) {
  const result = await loadProduct(ui.state.productId);
  if (ui.dead) return;
  ui.loading = false;
  if (!result.ok) {
    toast(result.error || 'Could not load that product', 'error');
    Object.assign(ui.state, freshState());
    applyDraft(ui.state);
    syncWizardUrl('', 1);
    emitShellMeta(ui);
    paint(ui);
    return;
  }
  const step = ui.state.step;
  const connectSource = ui.state.connectSource;
  Object.assign(ui.state, result.state);
  ui.state.step = step >= 1 && step <= 3 ? step : 1;
  if (ui.state.step > 1 && !ui.state.productId) ui.state.step = 1;
  ui.state.connectSource = connectSource && IN_FLOW_SOURCES[connectSource] ? connectSource : '';
  if (ui.state.connectSource && ui.state.step !== 3) ui.state.step = 3;
  syncWizardUrl(ui.state.productId, ui.state.step, ui.state.connectSource);
  emitShellMeta(ui);
  paint(ui);
}

function paint(ui) {
  const { state } = ui;
  clear(ui.head);
  clear(ui.rail);
  clear(ui.body);

  const connecting = !!(state.connectSource && IN_FLOW_SOURCES[state.connectSource]);

  // Connect keeps the setup rail (Product details ✓, Context ✓, 03 First connector)
  // as in rendered/OnboardingGA4.html — phone stacks the rail above the form.
  if (!connecting && state.step !== 3) ui.head.append(header(state));
  ui.rail.append(rail(ui));

  if (ui.loading) {
    ui.body.append(skeleton(5));
    return;
  }
  if (connecting) {
    ui.body.append(connectView(ui));
    return;
  }
  if (state.step === 2) ui.body.append(filesView(ui));
  else if (state.step === 3) ui.body.append(connectorsView(ui));
  else ui.body.append(detailsView(ui));
}

function header(state) {
  // Connectors step paints its own title + depth meter in the body.
  if (state.step === 3) return el('div');
  const copy = headerCopy(state.step);
  return el('header', { class: 'np-head rv' },
    el('div', { class: 'np-head-row' },
      el('div', { class: 'np-head-copy' },
        el('h1', { class: 'np-title' }, copy.title),
        el('p', { class: 'np-lead' }, copy.lead)),
      state.step > 1
        ? el('div', { class: 'np-step-tag' }, `Step ${state.step} of 3`)
        : null));
}

function headerCopy(step) {
  if (step === 2) {
    return {
      title: elFragment('Add anything you\'d hand ', el('em', {}, 'a new hire.')),
      lead: 'PRDs, specs, flows, screenshots. Files only — a shared link would break the moment its permissions change.',
    };
  }
  if (step === 3) {
    return {
      title: elFragment('Connect the tools ', el('em', {}, 'that matter.')),
      lead: 'Start with analytics. Everything else builds depth around the same product.',
    };
  }
  return {
    title: elFragment('Tell Bonnie about ', el('em', {}, 'the product.')),
    lead: 'This copy shapes how every insight is phrased. Two minutes now saves vague answers later.',
  };
}

function elFragment(...nodes) {
  return nodes;
}

function rail(ui) {
  const items = stepItems(ui.state.step, ui.state.productId);
  return el('nav', { class: 'np-rail rv np-rv-1', 'aria-label': 'Setup steps' },
    el('span', { class: 'np-lbl' }, `Setup · step ${ui.state.step} of 3`),
    el('div', {
      class: 'np-progress',
      'aria-hidden': 'true',
    }, el('div', {
      class: 'np-progress-fill grow',
      style: `width:${progressPct(ui.state.step)}%`,
    })),
    el('ol', { class: 'np-steps' },
      items.map((item) => el('li', {
        class: item.on ? 'np-step is-on' : (item.done ? 'np-step is-done' : 'np-step'),
      },
      el('button', {
        type: 'button',
        class: 'np-step-btn',
        disabled: item.locked ? true : null,
        'aria-current': item.on ? 'step' : null,
        title: item.locked ? 'Save the product first' : null,
        onClick() { goStep(ui, item.n); },
      },
      el('span', { class: item.on ? 'np-num is-on' : 'np-num' },
        item.done ? '✓' : String(item.n).padStart(2, '0')),
      el('span', { class: 'np-step-label' }, item.label))))),
    el('p', { class: 'np-rail-note' },
      'Everything here is editable later from the product\'s context panel.'));
}

function detailsView(ui) {
  return renderDetails(ui.state, {
    onChange() {
      // Inputs and chips update their own DOM; only keep state and the draft
      // in sync. Repainting here rebuilt the page and replayed its entrance
      // animation on every keystroke and selection.
      const industry = ui.root.querySelector('#np-industry');
      const name = ui.root.querySelector('#np-name');
      const intro = ui.root.querySelector('#np-intro');
      if (name) ui.state.name = name.value;
      if (intro) ui.state.intro = intro.value;
      if (industry) ui.state.storeCategory = industry.value;
      writeDraft(ui.state);
    },
    onContinue: () => persist(ui, true),
    onDraft: () => persist(ui, false),
  });
}

function filesView(ui) {
  return renderFiles(ui.state, {
    onContinue() {
      ui.state.step = 3;
      ui.state.connectSource = '';
      syncWizardUrl(ui.state.productId, 3);
      emitShellMeta(ui);
      paint(ui);
    },
    onJobs() { paintFiles(ui); },
  });
}

function paintFiles(ui) {
  const host = ui.body.querySelector('.np-files');
  if (!host) {
    paint(ui);
    return;
  }
  host.replaceWith(filesView(ui));
}

function connectorsView(ui) {
  return renderConnectorsStep(ui.state, {
    me: ui.opts.me,
    productName: ui.state.name || ui.opts.productName || 'this product',
    onSkip() {
      navigate('/app/dashboard');
    },
    onConnect(source) {
      openConnect(ui, source);
    },
  });
}

function connectView(ui) {
  const source = ui.state.connectSource;
  const meta = IN_FLOW_SOURCES[source];
  const placeholder = el('div', { class: 'np-connect' }, skeleton(4));
  resolveConnectItem(ui, meta && meta.provider).then((item) => {
    if (ui.dead) return;
    if (!item) {
      toast('That connector is not available', 'error');
      ui.state.connectSource = '';
      syncWizardUrl(ui.state.productId, 3);
      emitShellMeta(ui);
      paint(ui);
      return;
    }
    const view = renderConnectStep(ui.state, {
      me: ui.opts.me,
      productName: ui.state.name || ui.opts.productName || 'this product',
      item,
      source,
      onBack() {
        ui.state.connectSource = '';
        syncWizardUrl(ui.state.productId, 3);
        emitShellMeta(ui);
        paint(ui);
      },
      onConnected() {
        refreshShellProducts();
      },
    });
    placeholder.replaceWith(view);
  });
  return placeholder;
}

async function resolveConnectItem(ui, providerName) {
  if (!providerName || !ui.state.productId) return null;
  if (ui.catalogue.length) {
    return itemForSource(
      ui.catalogue.map((item) => ({ ...item, productId: ui.state.productId })),
      ui.state.connectSource,
    ) || ui.catalogue.find((item) => item.name === providerName) || null;
  }
  const bundle = await loadConnectorBundle(ui.state.productId);
  if (ui.dead) return null;
  const integrations = bundle.integrations && !bundle.integrations.error
    ? (bundle.integrations.integrations || [])
    : [];
  const providers = bundle.providers && !bundle.providers.error
    ? (bundle.providers.providers || [])
    : [];
  const servers = bundle.mcp && !bundle.mcp.error ? (bundle.mcp.servers || []) : [];
  ui.catalogue = assembleCatalogue(providers, integrations, servers);
  const found = ui.catalogue.find((item) => item.name === providerName);
  return found ? { ...found, productId: ui.state.productId } : null;
}

function openConnect(ui, source) {
  if (!IN_FLOW_SOURCES[source]) return;
  ui.state.step = 3;
  ui.state.connectSource = source;
  syncWizardUrl(ui.state.productId, 3, source);
  emitShellMeta(ui);
  paint(ui);
}

function goStep(ui, n) {
  if (n > 1 && !ui.state.productId) {
    toast('Save the product first', 'info');
    return;
  }
  ui.state.step = n;
  ui.state.connectSource = '';
  syncWizardUrl(ui.state.productId, n);
  emitShellMeta(ui);
  paint(ui);
}

async function persist(ui, advance) {
  if (ui.saving) return;
  const nameEl = ui.root.querySelector('#np-name');
  const introEl = ui.root.querySelector('#np-intro');
  const industryEl = ui.root.querySelector('#np-industry');
  if (nameEl) ui.state.name = nameEl.value;
  if (introEl) ui.state.intro = introEl.value;
  if (industryEl) ui.state.storeCategory = industryEl.value;

  ui.saving = true;
  setError(ui, '');
  const result = await saveProduct(ui.state);
  ui.saving = false;
  if (ui.dead) return;
  if (!result.ok) {
    setError(ui, result.error || 'Could not save the product');
    toast(result.error || 'Could not save the product', 'error');
    return;
  }
  acceptSaved(ui.state, result.product);
  try { localStorage.setItem('selectedProductId', result.product.id); } catch { /* ignore */ }
  if (advance) {
    ui.state.step = 2;
    ui.state.connectSource = '';
  }
  syncWizardUrl(ui.state.productId, ui.state.step);
  refreshShellProducts();
  emitShellMeta(ui);
  if (advance) paint(ui);
  else toast(result.editing ? 'Changes saved' : 'Draft saved', 'success');
}

function setError(ui, message) {
  const node = ui.root.querySelector('#np-error');
  if (!node) return;
  if (!message) {
    node.hidden = true;
    node.textContent = '';
    return;
  }
  node.hidden = false;
  node.textContent = message;
}

function emitShellMeta(ui) {
  const { state, opts } = ui;
  try {
    document.dispatchEvent(new CustomEvent('bonnie:wizard-meta', {
      detail: {
        crumb: wizardCrumb(state.step, state.connectSource),
        step: state.step,
        productId: state.productId,
        connectSource: state.connectSource || '',
        welcome: showWelcomeBadge((opts && opts.products) || [], state.productId, state.step),
      },
    }));
  } catch { /* ignore */ }
}
